import LoggerService from './logger-service';
import { LoggerViewModel } from './models';
import { LoggerInfo } from './logger-info';
import { modalUtils, toSafeArray } from '@/app/core/utils';
import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { Table } from 'antd';
import { TablePaginationConfig } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import {
  PaginationConfig,
  SearchRequest,
  SortConfig,
  SortDirection,
  Logger,
  LoggerSearchResponse,
} from '@ssar-app/domain';
import { Modal } from '@ssar-app/uikit';
import { HStack, Icon, Text, Tooltip } from '@chakra-ui/react';
import { AiOutlineInfoCircle } from 'react-icons/ai/index.js';

export const LogsList = () => {
  const [logs, setLogs] = useState<LoggerViewModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    hideOnSinglePage: false,
    defaultCurrent: 1,
    current: 1,
    total: 0,
  });

  const columns = useMemo(
    () =>
      [
        {
          title: '',
          align: 'center' as const,
          width: 40,
          render: (text: string, record: Logger) => {
            return (
              <Tooltip placement='top' label='Show log details' hasArrow>
                <a
                  href='/logger/info'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const modal = Modal.info({
                      closable: true,
                      icon: false,
                      showFooterBtns: false,
                      contentProps: { maxW: '80%' },
                      content: <></>,
                      scrollBehavior: 'outside',
                      bodyProps: { pb: 6 },
                    });
                    const closeModal = modal.destroy;
                    modal.update({
                      header: 'Log Information',
                      headerProps: { color: 'black', fontWeight: '500' },
                      content: <LoggerInfo id={record.id} onOk={closeModal} />,
                    });
                  }}
                >
                  <Icon
                    as={AiOutlineInfoCircle}
                    sx={{ h: '15px', w: '15px' }}
                  />
                </a>
              </Tooltip>
            );
          },
        },
        {
          title: 'User Name',
          align: 'center' as const,
          sorter: false,
          dataIndex: ['meta', 'user', 'name'],
        },
        {
          title: 'Email',
          sorter: false,
          dataIndex: ['meta', 'user', 'email'],
        },
        {
          title: 'Timestamp',
          sorter: false,
          dataIndex: 'timestamp',
        },
        {
          title: 'IP',
          dataIndex: ['meta', 'ip'],
          sorter: false,
        },
        {
          title: 'Level',
          dataIndex: 'level',
          sorter: false,
        },
        {
          title: 'URL',
          dataIndex: ['meta', 'req', 'url'],
          sorter: false,
        },
        {
          title: 'Method',
          align: 'center' as const,
          dataIndex: ['meta', 'req', 'method'],
          sorter: false,
        },
        {
          title: 'Status Code',
          align: 'center' as const,
          dataIndex: ['meta', 'res', 'statusCode'],
          sorter: false,
        },
        {
          title: 'Response Time',
          align: 'center' as const,
          dataIndex: ['meta', 'responseTime'],
          sorter: false,
        },
        {
          title: 'Request ID',
          align: 'center' as const,
          dataIndex: ['meta', 'req', 'headers', 'X-SSAR-REQUEST-ID'],
          sorter: false,
        },
      ].filter((a) => a !== null),
    [],
  );

  const fetchData = useCallback(
    (
      paginationInput: TablePaginationConfig = pagination,
      filters: Record<string, (Key | boolean)[] | null>,
      sorter:
        | SorterResult<LoggerViewModel>
        | SorterResult<LoggerViewModel>[] = [],
    ): void => {
      setIsLoading(true);

      const paginationConfig: PaginationConfig = {
        pageSize: paginationInput?.pageSize || 10,
        current: paginationInput?.current || 1,
      };

      const sortConfig: SortConfig[] = [];

      toSafeArray(sorter).forEach((a) => {
        if (a.field) {
          const dir =
            a.order === 'descend'
              ? SortDirection.Descending
              : a.order === 'ascend'
              ? SortDirection.Ascending
              : null;
          if (dir) {
            sortConfig.push({
              field: a.field.toString(),
              direction: dir,
            });
          }
        }
      });

      const searchModel: SearchRequest = {
        paging: paginationConfig,
        sortBy: sortConfig,
        filter: filters,
      };

      LoggerService.getLogs(searchModel)
        .then((logsResponse: LoggerSearchResponse) => {
          const { paging, data } = logsResponse;
          const mergedPagination = {
            ...pagination,
            ...paging,
          };
          mergedPagination.current = +mergedPagination.current;
          mergedPagination.pageSize = +mergedPagination.pageSize;
          setPagination(mergedPagination);
          setLogs(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (data || []).map((a: any) => ({
              ...a,
              name: a.meta.user.name,
            })),
          );
        })
        .catch((err) => {
          console.error(err);
          modalUtils.showError(err, 'Error while getting Logs');
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [pagination],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDataWrapper = useCallback(() => fetchData(pagination, {}, []), []);

  useEffect(() => {
    fetchDataWrapper();
  }, [fetchDataWrapper]);

  // modalUtils.showSuccessMessage('The data was saved successfully.');
  return (
    <div>
      {logs && (pagination?.total || 1) > 0 ? (
        <>
          <HStack justify='space-between'>
            <Text
              sx={{
                fontSize: 'xl',
                fontWeight: 'semibold',
                mb: 4,
                color: 'black',
              }}
            >
              Logs
            </Text>
          </HStack>
          <Table
            onChange={fetchData}
            showSorterTooltip={false}
            className='table-data-list logs-table'
            size='small'
            bordered={true}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rowKey={(row) => row.id || (row as any)['_id']}
            loading={isLoading}
            dataSource={logs}
            columns={columns}
            pagination={{
              ...pagination,
              position: ['bottomLeft'],
              showSizeChanger: true,
              simple: false,
            }}
          ></Table>
        </>
      ) : (
        <div>No Permission.</div>
      )}
    </div>
  );
};
